import type { ActionFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import {
  Link,
  useFetcher,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "@remix-run/react";
import { addToCart } from "~/cart.server";

import { ImageCarousel } from "~/components/Shop/ImageCarousel";
import { getUser } from "~/session.server";
import type { CartItem } from "../../../../types/CartItem";
import { GetVariationUrl } from "~/utils/images";
import { formatPrice } from "~/utils/strings";
import {
  addToWaitingList,
  getInventoryForVariations,
  getProductById,
  sendProductViewedToQueue,
} from "./queries";
import { validateCartItem } from "./validate";
import { INTENTS } from "~/utils/intents";
import { validatePhoneNumber } from "~/utils/validators";
import { LinkTo } from "~/assets/icons/LinkTo";
import { useModal } from "~/context/Modal";
import { AddedToShoppingCart } from "~/components/AddedToShoppingCart";
import { CrossSell } from "../../../../types/CrossSell";
import { logAddedToCart } from "~/klaviyo.server";
import { ProductRow } from "~/components/frontpage/ProductRow";
import { calculatePrice } from "~/utils/price";
import { getDiscount } from "~/utils/discount";
import { ProductDetailsKV, VariationKV } from "../../../../types/KV/ProductDetailsSchema";
import { Inventory } from "../../../../types/Inventory";

export const meta = ({
  data,
}: {
  data: {
    data: ProductDetailsKV
    crossSells: CrossSell[];
  };
}) => {
  return [
    { title: data.data.name },
    { name: "theme-color", content: "#E3DACC" },
  ];
};

export const loader = async (args: LoaderFunctionArgs) => {
  const user = await getUser(args.request);
  const params = args.params as { id: string };
  const url = new URL(args.request.url);
  const variation = url.searchParams.get("variation");

  let product = await getProductById(
    args.context.cloudflare.env.PRODUCT_DETAILS as KVNamespace,
    params.id,
  );
  const inventory = await args.context.cloudflare.env.INVENTORY.get<Inventory>(
    variation || product.id,
    "json",
  );

  const hasVariations = product.variations.length > 1;
  let variationInventory: Inventory[] = []
  if (hasVariations) {
    variationInventory = await getInventoryForVariations(args.context.cloudflare.env.INVENTORY, product.variations.map(v => v.id));
  }
  const variationItem = product.variations.find(v => v.id === variation);

  const soldOut = hasVariations ? variationItem ? (variationInventory.find(i => i.external_id === variationItem.externalId)?.quantity || 0) <= 0 : false : inventory ? inventory.quantity <= 0 : false;
  await sendProductViewedToQueue(args.context, product, user);
  return json({
    data: product,
    soldOut,
    variationInventory
  });
};

export const action: ActionFunction = async ({ request, context }) => {
  const formData = await request.formData();
  const intent = String(formData.get("intent"));
  const user = await getUser(request);

  if (intent === INTENTS.ADD_CROSS_SELL_TO_CART) {
    const itemStr = String(formData.get("item"));
    const crossSell = JSON.parse(itemStr) as CrossSell;
    if (!crossSell || !crossSell.externalId) {
      return json(
        { message: "Ekki hægt að bæta vöru í körfu" },
        { status: 400 },
      );
    }
    const cartItem: Pick<CartItem, "product" | "quantity"> = {
      product: {
        id: crossSell.id,
        externalId: crossSell.externalId,
        brand: crossSell.brand,
        title: crossSell.name,
        categories: [],
        priceFrom: crossSell.priceFrom,
        image: { id: crossSell.imageUrl },
        slug: crossSell.slug,
      },
      quantity: 1,
    };
    const cart = await addToCart(
      user.id,
      cartItem,
      context.cloudflare.env.DB as D1Database,
    );
    if (user.email) {
      logAddedToCart(context, user.email, cart, cartItem);
    }
    return json({ success: true });
  }
  if (intent === INTENTS.ADD_TO_CART) {
    const itemStr = String(formData.get("item"));
    const hasVariations = String(formData.get("hasVariations")) === "true";
    const soldOut = String(formData.get("soldOut")) === "true";
    if (itemStr) {
      const item: Pick<CartItem, "product" | "quantity"> = JSON.parse(itemStr);
      const err = validateCartItem(item, hasVariations, soldOut);
      if (err) {
        return json({ message: err.message }, { status: err.status });
      }

      const cart = await addToCart(
        user.id,
        item,
        context.cloudflare.env.DB as D1Database,
      );
      if (user.email) {
        logAddedToCart(context, user.email, cart, item);
      }
      return json({
        success: true,
        item,
      });
    } else {
      throw new Response("Bad Request", { status: 400 });
    }
  } else if (intent === INTENTS.ADD_TO_WAITING_LIST) {
    const phoneNumber = String(formData.get("phoneNumber"));
    const productId = String(formData.get("productId"));
    if (validatePhoneNumber(phoneNumber)) {
      await addToWaitingList(context.cloudflare.env.DB, phoneNumber, productId);
      return json({
        success: true,
      });
    } else {
      return json({ error: "Ógilt símanúmer!" }, { status: 400 });
    }
  } else {
    throw new Response("Bad Request", { status: 400 });
  }
};

export default function Index() {
  const fetcher = useFetcher<{
    message: string;
    success: boolean;
    item?: CartItem;
  }>();

  const [searchParams] = useSearchParams();
  const { data, soldOut, variationInventory } = useLoaderData<{
    data: ProductDetailsKV;
    soldOut:
    | boolean
    variationInventory: Inventory[]
  }>();

  const { setShoppingCartVisible } = useModal();
  const hasVariations = data.variations.length > 1;
  const variationId = searchParams.get("variation");
  const variation = data.variations.find((v) => v.id === variationId);
  const price = variation ? variation.price : data.priceFrom;
  const discount = getDiscount(
    data.brand.discount,
    data.discount,
    variation?.discount,
  );
  const priceInfo = calculatePrice(price, discount);
  const selectedImageUrl = variation ? variation.imageUrl : data.imageUrl;

  const cartItem: Pick<CartItem, "product" | "quantity"> = {
    quantity: 1,
    product: {
      id: data.id,
      externalId: variation ? variation.externalId : data.externalId,
      brand: data.brand.name,
      title: data.name,
      categories: data.categories.map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
      })),
      priceFrom: variation ? variation.price : data.priceFrom,
      discount: discount,
      variation: variation
        ? {
          id: variation.id,
          title: variation.attributes[0],
        }
        : undefined,
      image: { id: variation ? variation.imageUrl : (data.imageUrl || '') },//TODO: this is a smell, cartitem should probably be using ProductDetailsKV
      slug: data.slug,
    },
  };

  if (data.hidden) {
    return (
      <div className="flex flex-col bg-[#E3DACC]">
        <div className="h-20 w-full bg-[#E3DACC]"></div>
        <div className="h-full bg-white">
          <div className="flex h-screen w-screen flex-row items-center justify-center">
            Því miður þá virðist þessi vara ekki vera til 😢
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-[#E3DACC]">
      <div className="h-20 w-full bg-[#E3DACC]"></div>
      <div className="h-full bg-white">
        <div className="my-10 mt-10 grid grid-cols-1 gap-4 bg-white px-4 md:gap-10 lg:mt-32 xl:grid-cols-[600px_1fr] xl:px-[180px]">
          <div className="relative">
            <ImageCarousel
              images={data.images}
              selectedImageUrl={selectedImageUrl}
              soldOut={soldOut}
              style={{ viewTransitionName: "product-image" }}
            />
          </div>

          <div className="flex-col lg:ml-[180px]">
            <div className="mt-4 flex flex-col md:mt-16 lg:mt-10">
              <div className="font-FKScreamer text-[48px] font-normal uppercase leading-[40px] text-[#C0A57E]">
                {data.brand.name}
              </div>
              <div className="mt-5 font-FKScreamer text-[36px] font-normal uppercase leading-[30px] text-[#0E0F0F]">
                {data.name}
              </div>
              <div className="mt-5 flex flex-row items-center gap-x-4">
                <div
                  className={`text-[20px] font-bold leading-[22px] text-[#0E0F0F] ${priceInfo.hasDiscount ? "line-through" : ""}`}
                >
                  {formatPrice(priceInfo.priceFrom)}
                </div>
                {priceInfo.hasDiscount && (
                  <div className="flex flex-row items-center gap-x-2">
                    <div className="text-[20px] font-bold leading-[22px] text-[#FF3636]">
                      {formatPrice(priceInfo.priceWithDiscount)}
                    </div>
                    <div className=" rounded-full bg-[#FF3636] px-[6px] py-[3px] text-[20px] font-bold leading-[22px] text-white">
                      {priceInfo.discountPercentage}%
                    </div>
                  </div>
                )}
              </div>
              <div
                className="hidden mt-5 sm:line-clamp-4 text-[16px] font-normal leading-[22px] text-[#0E0F0F] opacity-60 lg:whitespace-normal"
                dangerouslySetInnerHTML={{ __html: data.body }}
              />
              {hasVariations && (
                <Variations
                  variations={data.variations.map(v => {
                    const inv = variationInventory.find(i => i.external_id === v.externalId);
                    return {
                      ...v,
                      soldOut: v.soldOut || (inv ? inv.quantity <= 0 : false)
                    }

                  })}
                  variationId={variationId || undefined}
                />
              )}
              {fetcher.data && fetcher.data.message && (
                <div className="mb-4 inline-block w-min whitespace-nowrap bg-red-200 p-4 text-sm font-medium">
                  {fetcher.data.message}
                </div>
              )}
              <div className="mt-4 w-full md:w-[400px]">
                <fetcher.Form method="post">
                  <input
                    type="hidden"
                    name="intent"
                    value={INTENTS.ADD_TO_CART}
                  />

                  <input
                    type="hidden"
                    name="hasVariations"
                    value={String(hasVariations)}
                  />
                  <input type="hidden" name="soldOut" value={String(soldOut)} />
                  <input
                    type="hidden"
                    name="item"
                    value={JSON.stringify(cartItem)}
                  />
                  <div className="mt-4 flex w-fit flex-row">
                    <button
                      disabled={soldOut || fetcher.state === "submitting"}
                      onClick={() => {
                        setShoppingCartVisible("addedToShoppingCart");
                      }}
                    >
                      <div
                        className={`flex flex-row items-center gap-2 border-b-1 border-[#0E0F0F] text-xl font-medium ${soldOut || fetcher.state === "submitting" ? "opacity-30" : "opacity-1"}`}
                      >
                        Setja í körfu
                        <LinkTo fill={"#0E0F0F"} />
                      </div>
                    </button>
                  </div>
                </fetcher.Form>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-greeen-400 mx-6 my-[120px] grid grid-cols-1 gap-x-16 lg:mx-[180px] lg:grid-cols-2">
          <div>
            <div className="font-FKScreamer text-[64px] font-normal leading-[54px]">
              Lýsing
            </div>
            <div
              className="mt-8 text-[16px] font-normal leading-[22px]"
              dangerouslySetInnerHTML={{ __html: data.body }}
            ></div>
          </div>
          <div>
            {!isEmptyHtml(data.description) && (
              <>
                <div className="mt-10 text-[20px] font-bold leading-[22px] lg:mt-0">
                  Innihaldsefni
                </div>
                <div
                  className="mt-8 text-[16px] font-normal leading-[22px] opacity-60"
                  dangerouslySetInnerHTML={{
                    __html: data.description
                      ?.replace("Innihaldsefni:", "")
                      ?.replace("innihaldsefni:", "")
                      .replace("Ingredients:", ""), //TODO: need to do this in a one time batch execution
                  }}
                ></div>
              </>
            )}
          </div>
        </div>
        <div className="mb-[120px]">
          {data.relatedProducts && data.relatedProducts.length > 0 && (
            <ProductRow title={"Tengdar vörur"} products={data.relatedProducts.sort((a, b) => a.orderIndex - b.orderIndex).map(rp => {
              return {
                imageUrl: rp.imageUrl,
                brand: { name: rp.brand.name, discount: rp.brand.discount },
                name: rp.name,
                slug: rp.slug,
                priceFrom: rp.priceFrom,
                discount: rp.discount,
                soldOut: rp.soldOut,

              }
            })} />
          )}
        </div>
      </div>
      <AddedToShoppingCart
        item={fetcher.data?.item}
        crossSells={data.crossSells || []}
        isCalendarItem={false}
      />
    </div>
  );
}

const Variations = ({
  variationId,
  variations,
}: {
  variationId?: string;
  variations: (VariationKV)[];
}) => {
  const { pathname } = useLocation();
  const sortedVariations =
    variations.sort((a, b) => {
      if (a.orderIndex === undefined || b.orderIndex === undefined) {
        return 0;
      }
      return a.orderIndex - b.orderIndex
    })

  const selectedVariation = variations.find((v) => v.id === variationId);
  return (
    <div className="mt-10" >
      <div className="mb-2 text-xl font-semibold">
        {selectedVariation?.attributes[0] || "-"}
      </div>

      <div className="mb-10 grid max-w-[328px] grid-cols-6 gap-2">
        {sortedVariations.map((variation) => (
          <Link
            replace
            preventScrollReset={true}
            key={variation.id}
            to={pathname + "?variation=" + variation.id}
            className={`flex cursor-pointer flex-col items-center justify-center text-center ${variation.soldOut ? "opacity-30" : ""
              } ${variation.id === variationId
                ? "rounded-sm border-2 border-gray-500"
                : ""
              }`}
          >
            <img
              className="h-12 w-12 object-contain"
              alt={variation.attributes[0]}
              src={GetVariationUrl(variation.imageUrl)}
            />
          </Link>
        ))}
      </div>
    </div >
  );
};

function isEmptyHtml(html: string) {
  const text = html.replace(/<[^>]*>/g, '').trim();
  return text === '';
}